import type { ComponentPropsWithoutRef, ReactNode } from 'react'

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

function MdxH1(props: ComponentPropsWithoutRef<'h1'>) {
  const id =
    typeof props.children === 'string' ? slugify(props.children) : undefined
  return (
    <h1
      id={id}
      className="text-3xl md:text-4xl font-bold font-display text-[var(--color-text-primary)] mt-10 mb-4 scroll-mt-20"
      {...props}
    />
  )
}

function MdxH2(props: ComponentPropsWithoutRef<'h2'>) {
  const text = typeof props.children === 'string' ? props.children : undefined
  const id = text ? slugify(text) : undefined
  const { children, ...rest } = props
  return (
    <h2
      id={id}
      className="group text-2xl md:text-3xl font-bold font-display text-[var(--color-text-primary)] mt-8 mb-3 scroll-mt-20"
      {...rest}
    >
      {children}
      {id && (
        <a href={`#${id}`} className="heading-anchor" aria-hidden="true">
          #
        </a>
      )}
    </h2>
  )
}

function MdxH3(props: ComponentPropsWithoutRef<'h3'>) {
  const text = typeof props.children === 'string' ? props.children : undefined
  const id = text ? slugify(text) : undefined
  const { children, ...rest } = props
  return (
    <h3
      id={id}
      className="group text-xl md:text-2xl font-semibold font-display text-[var(--color-text-primary)] mt-6 mb-2 scroll-mt-20"
      {...rest}
    >
      {children}
      {id && (
        <a href={`#${id}`} className="heading-anchor" aria-hidden="true">
          #
        </a>
      )}
    </h3>
  )
}

function MdxParagraph(props: ComponentPropsWithoutRef<'p'>) {
  return (
    <p
      className="text-base leading-relaxed text-[var(--color-text-secondary)] mb-4"
      {...props}
    />
  )
}

function MdxAnchor(props: ComponentPropsWithoutRef<'a'>) {
  return (
    <a
      className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  )
}

function MdxBlockquote(props: ComponentPropsWithoutRef<'blockquote'>) {
  return (
    <blockquote
      className="border-l-4 border-primary/40 pl-4 my-6 italic text-[var(--color-text-tertiary)]"
      {...props}
    />
  )
}

function MdxCode(
  props: ComponentPropsWithoutRef<'code'> & { children?: ReactNode },
) {
  const isInline = !props.className
  if (isInline) {
    return (
      <code
        className="bg-[var(--color-bg-tertiary)] text-primary px-1.5 py-0.5 rounded text-sm font-mono"
        {...props}
      />
    )
  }
  // For code blocks, return children as-is (will be processed by pre/CodeBlock)
  return <code className={props.className} {...props} />
}

function MdxPre(
  props: ComponentPropsWithoutRef<'pre'> & { children?: ReactNode },
) {
  // Return basic pre for non-highlighted content
  return (
    <pre
      className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4 overflow-x-auto my-6 text-sm"
      {...props}
    />
  )
}

function MdxImage({
  className,
  alt,
  ...props
}: ComponentPropsWithoutRef<'img'>) {
  const hasIntrinsicSize = props.width !== undefined && props.height !== undefined
  // Markdown images without `![alt](...)` text land here with `alt`
  // undefined — fall back to the filename rather than shipping no alt at all.
  const resolvedAlt =
    alt || props.src?.split('/').pop()?.split('?')[0] || 'Image'

  if (hasIntrinsicSize) {
    // Author supplied real dimensions (e.g. small inline icons) — render at
    // that size instead of stretching it to fill the content column.
    return <img className={className} loading="lazy" alt={resolvedAlt} {...props} />
  }

  // Blog images are author-authored markdown with unknown intrinsic sizes, so
  // the wrapper reserves a 16:9 box up front to avoid layout shift while the
  // image loads.
  // A span (not a div) because MDX renders images inside a paragraph.
  return (
    <span className="block my-6 w-full overflow-hidden rounded-lg border border-[var(--color-border)] aspect-video">
      <img
        className={`h-full w-full object-contain ${className ?? ''}`}
        loading="lazy"
        alt={resolvedAlt}
        {...props}
      />
    </span>
  )
}

function MdxUl(props: ComponentPropsWithoutRef<'ul'>) {
  return (
    <ul
      className="list-disc list-inside space-y-1 mb-4 text-[var(--color-text-secondary)]"
      {...props}
    />
  )
}

function MdxOl(props: ComponentPropsWithoutRef<'ol'>) {
  return (
    <ol
      className="list-decimal list-inside space-y-1 mb-4 text-[var(--color-text-secondary)]"
      {...props}
    />
  )
}

function MdxHr(props: ComponentPropsWithoutRef<'hr'>) {
  return <hr className="border-[var(--color-border)] my-8" {...props} />
}

export const mdxComponents = {
  h1: MdxH1,
  h2: MdxH2,
  h3: MdxH3,
  p: MdxParagraph,
  a: MdxAnchor,
  blockquote: MdxBlockquote,
  code: MdxCode,
  pre: MdxPre,
  img: MdxImage,
  ul: MdxUl,
  ol: MdxOl,
  hr: MdxHr,
}
